import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Check, ChevronRight, ChevronLeft, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../lib/api";
import Grain from "../components/Grain";

const STEPS = ["Session", "Participant", "Paiement", "Confirmation"];

function formatDateFR(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "long", year: "numeric" });
}

export default function AtelierReservation() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = React.useState(0);
  const [session, setSession] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [form, setForm] = React.useState({
    prenom: "", nom: "", email: "", telephone: "",
    niveau_photo: "intermediaire", attentes: "",
    type_paiement: "integral",
    accept_conditions: false, confirm_prerequis: false, accept_late_policy: false,
  });

  React.useEffect(() => {
    api.get(`/sessions/${sessionId}`).then(r => setSession(r.data)).catch(() => setError("Session introuvable"));
  }, [sessionId]);

  if (error) return (
    <div className="pt-40 section-dark min-h-screen px-6 md:px-12 text-center">
      <h1 className="font-serif text-4xl italic">{error}</h1>
      <Link to="/atelier" className="btn-primary mt-8 inline-flex">Retour aux sessions</Link>
    </div>
  );

  if (!session) return (
    <div className="pt-40 section-dark min-h-screen flex items-center justify-center">
      <Loader2 size={32} className="animate-spin text-accent-deep" />
    </div>
  );

  const montant_paye = form.type_paiement === "integral" ? session.prix_actuel : Math.floor(session.prix_actuel / 2);
  const montant_restant = session.prix_actuel - montant_paye;

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const validParticipant = form.prenom && form.nom && form.email && form.telephone;
  const validPayment = form.accept_conditions && form.confirm_prerequis && form.accept_late_policy;

  const submit = async () => {
    setLoading(true);
    setError("");
    try {
      const origin = window.location.origin;
      const r = await api.post("/reservations/checkout", { session_id: sessionId, form, origin_url: origin });
      window.location.href = r.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  return (
    <div className="pt-28 section-dark min-h-screen">
      <section className="px-6 md:px-12 py-12 relative overflow-hidden" data-testid="reservation-page">
        <Grain/>
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] glow-amber-soft pointer-events-none"/>
        <div className="max-w-[1080px] mx-auto relative z-10">
          <div className="eyebrow mb-4">Réservation · L'Atelier Lumière</div>
          <h1 className="h-display text-4xl md:text-6xl">Quelques étapes,<br/><span className="italic-accent">et c'est à toi.</span></h1>

          {/* Stepper */}
          <div className="mt-12 flex items-center gap-3 md:gap-6 flex-wrap" data-testid="stepper">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <div className={`w-7 h-7 flex items-center justify-center text-xs ${i <= step ? "bg-accent-deep text-dark" : "border border-white/20 text-text-cream/40"}`}>
                  {i < step ? <Check size={12}/> : i + 1}
                </div>
                <span className={`eyebrow ${i === step ? "text-accent-light" : "text-text-cream/40"}`}>{s}</span>
                {i < STEPS.length - 1 && <div className={`hidden md:block h-px w-8 ${i < step ? "bg-accent-deep" : "bg-white/15"}`}/>}
              </div>
            ))}
          </div>

          <div className="mt-12">
            <AnimatePresence mode="wait">
              {step === 0 && <Step key="0" title="Ta session"><RecapSession session={session}/></Step>}
              {step === 1 && (
                <Step key="1" title="Tes coordonnées">
                  <Participant form={form} setForm={setForm}/>
                </Step>
              )}
              {step === 2 && (
                <Step key="2" title="Choix du paiement">
                  <Paiement form={form} setForm={setForm} session={session} montant_paye={montant_paye} montant_restant={montant_restant}/>
                </Step>
              )}
              {step === 3 && (
                <Step key="3" title="Récap final">
                  <RecapFinal form={form} session={session} montant_paye={montant_paye} montant_restant={montant_restant}/>
                </Step>
              )}
            </AnimatePresence>
          </div>

          {error && <div className="mt-6 text-red-400 text-sm">{error}</div>}

          <div className="mt-12 flex justify-between gap-4 flex-wrap">
            <button onClick={prev} disabled={step === 0} className="btn-secondary disabled:opacity-30" data-testid="step-prev">
              <ChevronLeft size={14}/> Précédent
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={next}
                disabled={(step === 1 && !validParticipant) || (step === 2 && !validPayment)}
                className="btn-primary disabled:opacity-40"
                data-testid="step-next"
              >
                Continuer <ChevronRight size={14}/>
              </button>
            ) : (
              <button onClick={submit} disabled={loading} className="btn-primary disabled:opacity-50" data-testid="step-pay">
                {loading ? <Loader2 size={14} className="animate-spin"/> : null} Payer maintenant <ChevronRight size={14}/>
              </button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Step({ title, children }) {
  return (
    <motion.div initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-16 }} transition={{ duration:0.4 }}>
      <h2 className="font-serif text-3xl md:text-4xl font-light mb-8">{title}</h2>
      {children}
    </motion.div>
  );
}

function RecapSession({ session }) {
  return (
    <div className="border border-white/10 p-8" data-testid="recap-session">
      <div className="eyebrow mb-2 text-accent-light">{session.workshop?.nom}</div>
      <div className="font-serif text-4xl italic">{formatDateFR(session.date)}</div>
      <div className="mt-4 space-y-2 text-text-cream/70 font-light">
        <div>{session.heure_debut} – {session.heure_fin}</div>
        <div>{session.lieu}</div>
        <div>{session.places_restantes} place{session.places_restantes > 1 ? "s" : ""} restante{session.places_restantes > 1 ? "s" : ""}</div>
      </div>
      <div className="mt-6 pt-6 border-t border-white/10 flex items-baseline justify-between">
        <span className="eyebrow">Prix</span>
        <span className="font-serif text-3xl">CHF {session.prix_actuel}</span>
      </div>
    </div>
  );
}

function Participant({ form, setForm }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="participant-form">
      <div>
        <label className="eyebrow mb-2 block">Prénom *</label>
        <input data-testid="res-prenom" className="input-editorial" value={form.prenom} onChange={set("prenom")} required/>
      </div>
      <div>
        <label className="eyebrow mb-2 block">Nom *</label>
        <input data-testid="res-nom" className="input-editorial" value={form.nom} onChange={set("nom")} required/>
      </div>
      <div>
        <label className="eyebrow mb-2 block">Email *</label>
        <input data-testid="res-email" type="email" className="input-editorial" value={form.email} onChange={set("email")} required/>
      </div>
      <div>
        <label className="eyebrow mb-2 block">Téléphone *</label>
        <input data-testid="res-telephone" className="input-editorial" value={form.telephone} onChange={set("telephone")} required/>
      </div>
      <div className="md:col-span-2">
        <label className="eyebrow mb-2 block">Niveau photo</label>
        <select data-testid="res-niveau" className="input-editorial bg-dark" value={form.niveau_photo} onChange={set("niveau_photo")}>
          <option value="debutant">Débutant</option>
          <option value="intermediaire">Intermédiaire</option>
          <option value="avance">Avancé</option>
          <option value="pro">Pro</option>
        </select>
      </div>
      <div className="md:col-span-2">
        <label className="eyebrow mb-2 block">Tes attentes (optionnel)</label>
        <textarea data-testid="res-attentes" rows={3} className="input-editorial resize-none" value={form.attentes} onChange={set("attentes")}/>
      </div>
    </div>
  );
}

function Paiement({ form, setForm, session, montant_paye, montant_restant }) {
  return (
    <div className="space-y-6" data-testid="payment-form">
      <div className="border border-accent-deep bg-accent-deep/5 p-8" data-testid="pay-integral-only">
        <div className="eyebrow text-accent-light mb-3">Paiement intégral</div>
        <div className="font-serif text-4xl italic text-accent-light">CHF {session.prix_actuel}</div>
        <p className="text-text-cream/70 font-light text-sm mt-4">
          Le montant total est réglé maintenant. Ta place est immédiatement confirmée et tu n'as plus rien à payer ensuite.
        </p>
        <p className="text-text-cream/50 font-light text-xs mt-3">
          Annulation gratuite jusqu'à 14 jours avant la session (100% remboursé). Voir les <Link to="/mentions-legales" className="underline">conditions complètes</Link>.
        </p>
      </div>

      <label className="flex items-start gap-3 mt-8 cursor-pointer" data-testid="check-conditions">
        <input type="checkbox" checked={form.accept_conditions} onChange={(e)=>setForm({...form, accept_conditions:e.target.checked})} className="mt-1 accent-[var(--accent-deep)]"/>
        <span className="text-text-cream/70 font-light text-sm">
          J'accepte les <Link to="/mentions-legales" className="underline">conditions générales</Link> et la <Link to="/politique-confidentialite" className="underline">politique de confidentialité</Link>, y compris la politique d'annulation et de report.
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer" data-testid="check-prerequis">
        <input type="checkbox" checked={form.confirm_prerequis} onChange={(e)=>setForm({...form, confirm_prerequis:e.target.checked})} className="mt-1 accent-[var(--accent-deep)]"/>
        <span className="text-text-cream/70 font-light text-sm">
          Je confirme maîtriser mon boîtier en mode manuel (vitesse, ouverture, ISO, mise au point).
        </span>
      </label>

      <label className="flex items-start gap-3 cursor-pointer" data-testid="check-late-policy">
        <input type="checkbox" checked={form.accept_late_policy} onChange={(e)=>setForm({...form, accept_late_policy:e.target.checked})} className="mt-1 accent-[var(--accent-deep)]"/>
        <span className="text-text-cream/70 font-light text-sm">
          J'ai pris connaissance de la <strong className="text-text-cream">politique de retard</strong> : tolérance de 15 minutes, au-delà la session continue sans rattrapage (pour respecter les autres participants).
        </span>
      </label>
    </div>
  );
}

function PaymentChoice({ selected, onClick, titre, montant, desc, tid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={tid}
      className={`text-left p-6 border transition ${selected ? "border-accent-deep bg-accent-deep/5" : "border-white/15 hover:border-white/30"}`}
    >
      <div className="flex items-center justify-between">
        <div className="font-serif text-2xl">{titre}</div>
        {selected && <Check size={18} className="text-accent-deep"/>}
      </div>
      <div className="font-serif text-3xl italic mt-2 text-accent-light">{montant}</div>
      <p className="text-text-cream/60 text-sm font-light mt-2">{desc}</p>
    </button>
  );
}

function RecapFinal({ form, session, montant_paye, montant_restant }) {
  return (
    <div className="space-y-6 border border-white/10 p-8" data-testid="recap-final">
      <div>
        <div className="eyebrow mb-2 text-accent-light">Session</div>
        <div className="font-serif text-2xl italic">{formatDateFR(session.date)} · {session.heure_debut}–{session.heure_fin}</div>
        <div className="text-text-cream/60 font-light text-sm mt-1">{session.lieu}</div>
      </div>
      <div className="border-t border-white/10 pt-6">
        <div className="eyebrow mb-2 text-accent-light">Participant</div>
        <div className="font-serif text-xl">{form.prenom} {form.nom}</div>
        <div className="text-text-cream/60 font-light text-sm">{form.email} · {form.telephone}</div>
      </div>
      <div className="border-t border-white/10 pt-6 grid grid-cols-2 gap-4">
        <div>
          <div className="eyebrow mb-2 text-accent-light">À payer maintenant</div>
          <div className="font-serif text-3xl">CHF {montant_paye}</div>
        </div>
        {form.type_paiement === "acompte" && (
          <div>
            <div className="eyebrow mb-2 text-accent-light">Solde (7j avant)</div>
            <div className="font-serif text-3xl text-text-cream/60">CHF {montant_restant}</div>
          </div>
        )}
      </div>
      <p className="text-text-cream/50 text-xs font-light">
        En confirmant, vous serez redirigé(e) vers Stripe Checkout pour finaliser le paiement.
        Vos données sont traitées conformément à la RGPD.
      </p>
    </div>
  );
}
